/**
 * Target-hotkey nameplates (#136) — the PURE, testable half.
 *
 * Each unit in the world view carries a small key label above its HP bar
 * showing WHICH hotkey direct-selects it (#63/#116 arena/party direct-select):
 * a friendly unit shows its PARTY key (green), an enemy shows its ARENA key
 * (red). This module resolves that label + faction; the Pixi draw lives in
 * `entityView.ts` (mirrors the combatText.ts / combatTextManager.ts split so
 * the mapping logic is unit-tested without Pixi).
 *
 * Determinism note: this is PURELY CLIENT-SIDE COSMETIC — derived from the
 * client keymap + the entity's roster membership, both already available
 * client-side. It touches no sim state, no hash, no protocol.
 *
 * Reuse, don't re-derive: the entity→index mapping goes through the SAME
 * roster resolvers the direct-select uses (`enemyTargetIndexWire` /
 * `allyTargetIndexWire`, which invert `@pit/sim`'s `enemyByIndex`/`allyByIndex`
 * from #63/#116), so the displayed letter can never diverge from the key that
 * actually selects the unit. Roster membership itself encodes faction — an
 * entity is in exactly one of (ally roster, enemy roster) relative to the
 * viewer — so we never re-derive team ordering here.
 */

import type { EntityId, WireSimState } from "@pit/protocol";
import type { Keymap } from "../input/keybinding/index.js";
import { targetHotkeyKeyLabel } from "../input/keybinding/index.js";
import {
  allyTargetIndexWire,
  enemyTargetIndexWire,
} from "../input/targetSelectWire.js";

/** Which team-relative roster the nameplate came from (drives the color). */
export type NameplateFaction = "friendly" | "enemy";

/**
 * Visual treatment for target-hotkey nameplates. One object, fully data-driven
 * (mirrors `CombatTextStyle`): swap the style to retheme with zero logic edits.
 * Colors default to the shipped combat-text palette (#48) for consistency —
 * heal-green for friendly, damage-red for enemy — and are bold + dark-outlined
 * so a single letter reads over a unit body at normal zoom.
 */
export interface NameplateStyle {
  /** Friendly (party) key color — any CSS/Pixi color string. */
  readonly friendlyColor: string;
  /** Enemy (arena) key color. */
  readonly enemyColor: string;
  /** Font size in px. */
  readonly fontSizePx: number;
  /** Font weight (bold for prominence). */
  readonly fontWeight: number;
  /** Dark outline color for contrast against unit bodies. */
  readonly outlineColor: string;
  /** Outline thickness in px (0 = no outline). */
  readonly outlinePx: number;
  /** Extra px the label sits ABOVE the HP bar top (clears the bar). */
  readonly offsetAbovePx: number;
}

/** Default treatment. Tunable single source of truth (like combatText). */
export const DEFAULT_NAMEPLATE_STYLE: NameplateStyle = {
  friendlyColor: "#54d06a",
  enemyColor: "#ff5040",
  fontSizePx: 14,
  fontWeight: 700,
  outlineColor: "#0a0f14",
  outlinePx: 3,
  offsetAbovePx: 4,
};

/** A resolved nameplate: the key label to draw + the faction (→ color). */
export interface Nameplate {
  readonly label: string;
  readonly faction: NameplateFaction;
}

/** The color to draw a nameplate faction in, per style. */
export function nameplateColor(
  faction: NameplateFaction,
  style: NameplateStyle,
): string {
  return faction === "friendly" ? style.friendlyColor : style.enemyColor;
}

/**
 * Resolve the target-hotkey nameplate for `entityId` from the perspective of
 * `ownSeat`, against the live `keymap`. Pure.
 *
 * An entity is in exactly one of the viewer's rosters (ally OR enemy) when it
 * is a live, directly-selectable unit; that membership picks the faction and
 * the roster index in one step. Returns `null` (graceful absence, no draw) when:
 *   • no snapshot, or the viewer's own seat isn't known yet (#731 — `null`
 *     means "unknown", NEVER defaulted to seat 0),
 *   • the entity is not live-targetable from `ownSeat` (dead / stealthed /
 *     out-of-roster — never in either roster), or
 *   • the resolved index has no bound key in the keymap (unbound / beyond the
 *     bound arena/party keys), or
 *   • the entity IS the viewer's own character (ally index 0 = self): a hotkey
 *     nameplate floating over your own unit is noise, so self is SUPPRESSED
 *     (locked decision, #136). Every other ally + all enemies still show.
 */
export function entityNameplate(
  snapshot: WireSimState | null,
  ownSeat: number | null,
  keymap: Keymap,
  entityId: EntityId,
): Nameplate | null {
  if (snapshot === null || ownSeat === null) return null;

  // Ally roster first (self = index 0). Membership here means friendly.
  const allyIndex = allyTargetIndexWire(snapshot, ownSeat, entityId);
  if (allyIndex !== null) {
    // SUPPRESS self (party slot 1 = ally index 0): no nameplate over the
    // viewer's own character (locked decision, #136).
    if (allyIndex === 0) return null;
    const label = targetHotkeyKeyLabel(keymap, "party", allyIndex);
    return label === null ? null : { label, faction: "friendly" };
  }

  // Otherwise the enemy roster. Membership here means enemy.
  const enemyIndex = enemyTargetIndexWire(snapshot, ownSeat, entityId);
  if (enemyIndex !== null) {
    const label = targetHotkeyKeyLabel(keymap, "arena", enemyIndex);
    return label === null ? null : { label, faction: "enemy" };
  }

  return null;
}

/**
 * Resolve every entity's nameplate for a snapshot into an `entityId → Nameplate`
 * map (entities with no nameplate are simply absent). Pure; called per frame by
 * the render loop, then looked up per entity in `updateEntityView`.
 */
export function buildNameplates(
  snapshot: WireSimState | null,
  ownSeat: number | null,
  keymap: Keymap,
): Map<string, Nameplate> {
  const map = new Map<string, Nameplate>();
  if (snapshot === null || ownSeat === null) return map;
  for (const e of snapshot.entities) {
    const np = entityNameplate(snapshot, ownSeat, keymap, e.entityId);
    if (np !== null) map.set(e.entityId, np);
  }
  return map;
}
